import { CrewRole } from "../../../shared/models/crewRole.model";
import { PilotRole } from "../../../shared/models/pilotRole.model";
import { Airport, OrganizationCrew, OrganizationListItem } from "../../../shared/bootstrap/bootstrap.model";
import { EditOption } from "../../models/edit-option.model";
import { Flight } from "../../models/flight.model";
import { MaxTank } from "../tabs/fuel-row/fuel-row";
import { displayNameOf, sameName } from "./names";

/**
 * The id a value the flight keeps only as text takes in the form, since it has none: it is never sent while the user leaves
 * it, and no list offers it.
 */
export const TEXT_ONLY_ID = -1;

export interface EditAirport {
    id: number;
    label: string;
    /** Absent for a place the flight keeps only as text. */
    airport: Airport | null;
}

export interface FlightEditLookupsInit {
    pilots: EditOption[];
    flightTypes: EditOption[];
    pilotRoles: PilotRole[];
    crewRoles: CrewRole[];
    airports: Airport[];
    crews: OrganizationCrew[];
    flightOperations: OrganizationListItem[];
    flightSpecifications: OrganizationListItem[];
    flightTypeNotes: OrganizationListItem[];
    fuelUnits: string[];
    oilUnits: string[];
    maxTank: MaxTank;
    flight: Flight;
    abbreviationKey: (abbreviation: unknown) => string;
}

export class FlightEditLookups {
    readonly pilots: EditOption[];
    readonly flightTypes: EditOption[];
    readonly pilotRoles: PilotRole[];
    readonly crewRoles: CrewRole[];
    readonly airports: EditAirport[];
    readonly crews: OrganizationCrew[];
    readonly flightOperations: OrganizationListItem[];
    readonly flightSpecifications: OrganizationListItem[];
    readonly flightTypeNotes: OrganizationListItem[];
    readonly fuelUnits: string[];
    readonly oilUnits: string[];
    /** The tanks of the flight's aircraft, when the organization checks the fuel against them. */
    readonly maxTank: MaxTank;
    /** The airports for the flight's departure and arrival, each with the place the flight already has. */
    readonly departures: EditAirport[];
    readonly arrivals: EditAirport[];
    /** The pilots for the flight's pilot and copilot, each with the name the flight already has. */
    readonly pilotChoices: EditOption[];
    readonly coPilotChoices: EditOption[];
    private readonly abbreviationKey: (abbreviation: unknown) => string;

    constructor(init: FlightEditLookupsInit) {
        this.pilots = init.pilots;
        this.flightTypes = init.flightTypes;
        this.pilotRoles = init.pilotRoles;
        this.crewRoles = init.crewRoles;
        this.airports = init.airports.map((airport) => ({ id: airport.id, label: displayNameOf(airport), airport: airport }));
        this.crews = init.crews;
        this.flightOperations = init.flightOperations;
        this.flightSpecifications = init.flightSpecifications;
        this.flightTypeNotes = init.flightTypeNotes;
        this.fuelUnits = init.fuelUnits;
        this.oilUnits = init.oilUnits;
        this.maxTank = init.maxTank;
        this.abbreviationKey = init.abbreviationKey;
        const { flight } = init;
        this.departures = this.airportsWith(flight.departureId, flight.departurePlace);
        this.arrivals = this.airportsWith(flight.arrivalId, flight.arrivalPlace);
        this.pilotChoices = this.pilotsWith(flight.pilotId, flight.pilot1Name, true);
        this.coPilotChoices = this.pilotsWith(flight.coPilotId, flight.pilot2Name, false);
    }

    /** The flight type of a ground run, which `GR` names: a flight is one by its type, and never becomes or stops being one. */
    get groundRunTypeId(): number | null {
        return this.flightTypes.find((type) => sameName(type.name, "GR"))?.id ?? null;
    }

    isGroundRun(flight: Flight): boolean {
        return sameName(flight.flightType, "GR");
    }

    airportOf(airportId: number | null): EditAirport | null {
        return this.airports.find((airport) => airport.id === airportId) ?? null;
    }

    /** The airports, with the one the flight has shown as the logbook wrote it, or a place it keeps only as text. */
    private airportsWith(id: number | null, place: string | null): EditAirport[] {
        const text = place?.trim();
        if (!text) return this.airports;
        if (id === null) return [{ id: TEXT_ONLY_ID, label: text, airport: null }, ...this.airports];
        return this.airports.map((airport) => (airport.id === id ? { ...airport, label: text } : airport));
    }

    /** The pilots, with the one the flight has shown as the logbook wrote it, even when it is no longer active. */
    private pilotsWith(id: number | null, name: string | null, textOnly: boolean): EditOption[] {
        const text = name?.trim();
        if (!text) return this.pilots;
        if (id === null) return textOnly ? [{ id: TEXT_ONLY_ID, name: text }, ...this.pilots] : this.pilots;
        return this.pilots.some((pilot) => pilot.id === id) ? this.pilots.map((pilot) => (pilot.id === id ? { ...pilot, name: text } : pilot)) : [{ id: id, name: text }, ...this.pilots];
    }

    flightTypeIdOf(name: string | null): number | null {
        return this.flightTypes.find((type) => sameName(type.name, name))?.id ?? null;
    }

    roleIdOf(abbreviation: string | null): number | null {
        const key = this.abbreviationKey(abbreviation);
        return this.pilotRoles.find((role) => this.abbreviationKey(role.abbreviation) === key)?.id ?? null;
    }

    crewRoleIdOf(name: string | null): number | null {
        return this.crewRoles.find((role) => sameName(role.name, name))?.id ?? null;
    }

    /** The copilot of a flight as it was logged: the name it keeps, with the pilot's id when it is one of the organization's. */
    coPilotOf(flight: Flight): EditOption | null {
        const name = flight.pilot2Name?.trim();
        return name ? { id: flight.coPilotId, name: name } : null;
    }

    /** The organization's crew members, and any one already on the flight that is no longer active. */
    crewMembersOf(flight: Flight): EditOption[] {
        const active = this.crews.map((crew) => ({ id: crew.id, name: `${crew.firstName} ${crew.lastName}` }));
        const known = new Set(active.map((crew) => crew.id));
        const others = flight.crewData
            .filter((member) => member.crewId !== null && !known.has(member.crewId))
            .map((member) => ({ id: member.crewId, name: [member.firstName, member.lastName].filter((part) => !!part?.trim()).join(" ") }));
        return [...active, ...others];
    }
}
